import React, { useState, useEffect, useMemo } from 'react';
import { getGameContexts, addGameContext, updateGameContext, deleteGameContext } from '../../services/gameContextsService';
import { supabase } from '../../utils/supabase';
import Button from '../common/Button';
import Modal from '../common/Modal';
import ModalContent from '../common/ModalContent';
import ModalCloseButton from '../common/ModalCloseButton';

const LABEL_CLASS = 'block text-sm font-medium text-text-secondary mb-1';
const FIELD_CLASS =
  'w-full px-3 py-2 rounded-md border border-border bg-surface text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary';

const GameContextLinks = ({ gameId, currentUserId, clubId = null, gameParticipantIds = [], onLinksUpdated }) => {
  const [contexts, setContexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContext, setEditingContext] = useState(null);
  const [currentPlayerId, setCurrentPlayerId] = useState(null);
  const [formData, setFormData] = useState({
    url: '',
    description: '',
    visibility: 'private',
    sourceType: 'other'
  });

  // Get player ID from auth user ID
  useEffect(() => {
    const getPlayerId = async () => {
      if (!currentUserId) {
        setCurrentPlayerId(null);
        return;
      }
      
      const { data, error } = await supabase
        .from('players')
        .select('id')
        .eq('auth_id', currentUserId)
        .single();
        
      if (error) {
        console.error('Error getting player ID:', error);
        setCurrentPlayerId(null);
      } else {
        setCurrentPlayerId(data?.id || null);
      }
    };
    
    getPlayerId();
  }, [currentUserId]);

  // Memoize participant IDs string to prevent unnecessary re-renders
  const participantIdsKey = useMemo(() => 
    JSON.stringify([...gameParticipantIds].sort()), 
    [gameParticipantIds]
  );

  useEffect(() => {
    if (gameId && currentUserId) {
      loadContexts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, currentUserId, clubId, participantIdsKey]);

  const loadContexts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getGameContexts(gameId, currentUserId, clubId, gameParticipantIds);
      setContexts(data || []);
    } catch (err) {
      console.error('Error loading game contexts:', err);
      setError('Failed to load game contexts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddContext = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      await addGameContext({
        gameId,
        userId: currentUserId,
        url: formData.url,
        description: formData.description,
        visibility: formData.visibility,
        sourceType: formData.sourceType
      });
      setFormData({ url: '', description: '', visibility: 'private', sourceType: 'other' });
      setShowAddModal(false);
      await loadContexts();
      if (onLinksUpdated) onLinksUpdated();
    } catch (err) {
      console.error('Error adding context:', err);
      setError(err.message || 'Failed to add context');
    }
  };

  const handleEditContext = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      await updateGameContext(editingContext.id, currentUserId, {
        url: formData.url,
        description: formData.description,
        visibility: formData.visibility,
        sourceType: formData.sourceType
      });
      setEditingContext(null);
      setFormData({ url: '', description: '', visibility: 'private', sourceType: 'other' });
      await loadContexts();
      if (onLinksUpdated) onLinksUpdated();
    } catch (err) {
      console.error('Error updating context:', err);
      setError(err.message || 'Failed to update context');
    }
  };

  const handleDeleteContext = async (contextId) => {
    if (!window.confirm('Are you sure you want to delete this link?')) {
      return;
    }
    try {
      setError(null);
      await deleteGameContext(contextId, currentUserId);
      await loadContexts();
      if (onLinksUpdated) onLinksUpdated();
    } catch (err) {
      console.error('Error deleting context:', err);
      setError(err.message || 'Failed to delete context');
    }
  };

  const openEditModal = (context) => {
    setEditingContext(context);
    setFormData({
      url: context.url,
      description: context.description || '',
      visibility: context.visibility,
      sourceType: context.source_type || 'other'
    });
  };

  const closeModals = () => {
    setShowAddModal(false);
    setEditingContext(null);
    setFormData({ url: '', description: '', visibility: 'private', sourceType: 'other' });
    setError(null);
  };

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'private':
        return '🔒';
      case 'game':
        return '👥';
      case 'club':
        return '🏢';
      case 'public':
        return '🌐';
      default:
        return '🔗';
    }
  };

  const getSourceTypeIcon = (sourceType) => {
    switch (sourceType) {
      case 'youtube':
        return '▶️';
      case 'google_drive':
        return '📁';
      case 'tournament_page':
        return '🏆';
      case 'manual_notes':
        return '📝';
      default:
        return '🔗';
    }
  };

  if (loading) {
    return <div className="text-sm text-text-muted">Loading links...</div>;
  }

  return (
    <div className="space-y-2">
      {error && (
        <div className="rounded border border-danger bg-danger-light p-2 text-sm text-danger-dark">
          {error}
        </div>
      )}

      {contexts.length === 0 ? (
        // Empty state with centered call-to-action
        <div className="rounded-lg border-2 border-dashed border-border-medium bg-surface-subtle py-4 px-4 text-center">
          <div className="flex flex-col items-center gap-2">
            <div className="text-text-muted">
              <svg className="mx-auto h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <div>
              <p className="mb-0.5 text-sm font-medium text-text-primary">No links added yet</p>
              <p className="mb-3 text-xs text-text-secondary">Add a link to share videos, notes, or other context</p>
            </div>
            <Button
              size="small"
              variant="primary"
              onClick={() => setShowAddModal(true)}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Link
              </span>
            </Button>
          </div>
        </div>
      ) : (
        // When links exist, show header with button
        <>
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-medium text-text-primary">Game Links</h4>
            <Button
              size="small"
              variant="ghost"
              onClick={() => setShowAddModal(true)}
            >
              + Add Link
            </Button>
          </div>
          <div className="space-y-2">
            {contexts.map((context) => (
              <div
                key={context.id}
                className="flex items-start justify-between rounded border border-border-medium bg-surface-subtle p-2 transition-colors hover:bg-[var(--color-hover)]"
              >
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <span>{getSourceTypeIcon(context.source_type)}</span>
                    <span>{getVisibilityIcon(context.visibility)}</span>
                    <a
                      href={context.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-sm text-primary hover:text-primary-dark hover:underline"
                    >
                      {context.url}
                    </a>
                  </div>
                  {context.description && (
                    <p className="mt-1 text-xs text-text-secondary">{context.description}</p>
                  )}
                  <p className="mt-1 text-xs text-text-muted">
                    Added by {context.user?.first_name} {context.user?.last_name}
                  </p>
                </div>
                {currentPlayerId && context.user_id === currentPlayerId && (
                  <div className="ml-2 flex gap-1">
                    <button
                      type="button"
                      onClick={() => openEditModal(context)}
                      className="rounded px-2 py-1 text-xs text-primary hover:bg-surface-elevated hover:text-primary-dark"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteContext(context.id)}
                      className="rounded px-2 py-1 text-xs text-danger hover:bg-danger-light hover:text-danger-dark"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add Context Modal */}
      <Modal isOpen={showAddModal} onClose={closeModals}>
        <ModalContent size="medium">
          <ModalCloseButton onClose={closeModals} />
          <h3 className="mb-4 text-lg font-semibold text-text-primary">Add Game Link</h3>
          <form onSubmit={handleAddContext}>
            <div className="space-y-4">
              <div>
                <label className={LABEL_CLASS}>
                  URL *
                </label>
                <input
                  type="url"
                  required
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className={FIELD_CLASS}
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className={LABEL_CLASS}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={FIELD_CLASS}
                  rows="3"
                  placeholder="Optional description..."
                />
              </div>
              <div>
                <label className={LABEL_CLASS}>
                  Visibility
                </label>
                <select
                  value={formData.visibility}
                  onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                  className={FIELD_CLASS}
                >
                  <option value="private">🔒 Private (Only me)</option>
                  <option value="game">👥 Game Participants</option>
                  <option value="club">🏢 Club Members</option>
                  <option value="public">🌐 Public (All users)</option>
                </select>
              </div>
              <div>
                <label className={LABEL_CLASS}>
                  Source Type
                </label>
                <select
                  value={formData.sourceType}
                  onChange={(e) => setFormData({ ...formData, sourceType: e.target.value })}
                  className={FIELD_CLASS}
                >
                  <option value="other">🔗 Other</option>
                  <option value="youtube">▶️ YouTube</option>
                  <option value="google_drive">📁 Google Drive</option>
                  <option value="tournament_page">🏆 Tournament Page</option>
                  <option value="manual_notes">📝 Manual Notes</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button type="button" variant="ghost" onClick={closeModals}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Add Link
              </Button>
            </div>
          </form>
        </ModalContent>
      </Modal>

      {/* Edit Context Modal */}
      <Modal isOpen={!!editingContext} onClose={closeModals}>
        <ModalContent size="medium">
          <ModalCloseButton onClose={closeModals} />
          <h3 className="mb-4 text-lg font-semibold text-text-primary">Edit Game Link</h3>
          <form onSubmit={handleEditContext}>
            <div className="space-y-4">
              <div>
                <label className={LABEL_CLASS}>
                  URL *
                </label>
                <input
                  type="url"
                  required
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className={FIELD_CLASS}
                />
              </div>
              <div>
                <label className={LABEL_CLASS}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={FIELD_CLASS}
                  rows="3"
                />
              </div>
              <div>
                <label className={LABEL_CLASS}>
                  Visibility
                </label>
                <select
                  value={formData.visibility}
                  onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                  className={FIELD_CLASS}
                >
                  <option value="private">🔒 Private (Only me)</option>
                  <option value="game">👥 Game Participants</option>
                  <option value="club">🏢 Club Members</option>
                  <option value="public">🌐 Public (All users)</option>
                </select>
              </div>
              <div>
                <label className={LABEL_CLASS}>
                  Source Type
                </label>
                <select
                  value={formData.sourceType}
                  onChange={(e) => setFormData({ ...formData, sourceType: e.target.value })}
                  className={FIELD_CLASS}
                >
                  <option value="other">🔗 Other</option>
                  <option value="youtube">▶️ YouTube</option>
                  <option value="google_drive">📁 Google Drive</option>
                  <option value="tournament_page">🏆 Tournament Page</option>
                  <option value="manual_notes">📝 Manual Notes</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button type="button" variant="ghost" onClick={closeModals}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Update Link
              </Button>
            </div>
          </form>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default GameContextLinks;

